import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../lib/auth';
import { rxAPI, pharmacyAPI, patientsAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Pill, Plus, Search, Trash2, Send, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

const STATUS_BADGE = {
  pending: 'bg-amber-100 text-amber-700 border-amber-300',
  dispensing: 'bg-blue-100 text-blue-700 border-blue-300',
  dispensed: 'bg-emerald-100 text-emerald-700 border-emerald-300',
};

export default function PrescriptionsPage() {
  const { user, hasPermission } = useAuth();
  const [rxList, setRxList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [createOpen, setCreateOpen] = useState(false);

  const canCreate = hasPermission('prescriptions.create');
  const canDispense = hasPermission('pharmacy.dispense');

  const fetchRx = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter) params.status_filter = statusFilter;
      const r = await rxAPI.list(params);
      setRxList(r.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load prescriptions');
    } finally { setLoading(false); }
  }, [statusFilter]);

  useEffect(() => { fetchRx(); }, [fetchRx]);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return rxList;
    return rxList.filter(r =>
      (r.rx_no || '').toLowerCase().includes(s) ||
      (r.patient_name || '').toLowerCase().includes(s) ||
      (r.doctor_name || '').toLowerCase().includes(s)
    );
  }, [rxList, search]);

  const handToPharmacy = async (rx) => {
    try {
      await rxAPI.handToPharmacy(rx.id);
      toast.success(`RX ${rx.rx_no} handed to pharmacy — open Pharmacy to dispense`);
      fetchRx();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to hand to pharmacy');
    }
  };

  if (!user?.is_super_admin && !hasPermission('prescriptions.view')) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center" data-testid="rx-restricted">
        <Card className="max-w-lg w-full">
          <CardHeader><CardTitle>Access Restricted</CardTitle></CardHeader>
          <CardContent>You don't have permission to view prescriptions.</CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="prescriptions-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2"><Pill className="text-emerald-600" /> Prescriptions</h1>
          <p className="text-sm text-slate-500">Doctor-issued Rx that pharmacists can dispense in one click.</p>
        </div>
        {canCreate && (
          <Button onClick={() => setCreateOpen(true)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="new-rx-btn">
            <Plus size={16} className="mr-1" /> New Prescription
          </Button>
        )}
      </div>

      <Card>
        <CardContent className="pt-4 space-y-3">
          <div className="flex flex-col md:flex-row gap-3">
            <div className="relative flex-1">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input data-testid="rx-search" className="pl-9" placeholder="Search by Rx No, patient, doctor…" value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
            <Select value={statusFilter || 'all'} onValueChange={(v) => setStatusFilter(v === 'all' ? '' : v)}>
              <SelectTrigger className="w-full md:w-48" data-testid="rx-status-filter"><SelectValue placeholder="All Statuses" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="dispensing">Handed to Pharmacy</SelectItem>
                <SelectItem value="dispensed">Dispensed</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {loading ? (
            <div className="text-sm text-slate-500 py-10 text-center">Loading prescriptions…</div>
          ) : filtered.length === 0 ? (
            <div className="text-sm text-slate-500 py-10 text-center" data-testid="rx-empty">No prescriptions yet. {canCreate && 'Click "New Prescription" to create one.'}</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-xs uppercase tracking-wider text-slate-500 border-b">
                  <tr><th className="text-left py-2 px-2">Rx No</th><th className="text-left py-2 px-2">Patient</th><th className="text-left py-2 px-2">Doctor</th><th className="text-left py-2 px-2">Medications</th><th className="text-left py-2 px-2">Status</th><th className="text-left py-2 px-2">Date</th><th className="text-right py-2 px-2">Actions</th></tr>
                </thead>
                <tbody>
                  {filtered.map(rx => (
                    <tr key={rx.id} className="border-b hover:bg-slate-50" data-testid={`rx-row-${rx.id}`}>
                      <td className="py-2 px-2 font-mono text-xs">{rx.rx_no}</td>
                      <td className="py-2 px-2">{rx.patient_name}</td>
                      <td className="py-2 px-2">{rx.doctor_name}</td>
                      <td className="py-2 px-2">
                        <div className="flex flex-wrap gap-1">
                          {(rx.medications || []).slice(0, 3).map((m, i) => (
                            <Badge key={i} variant="outline" className="text-xs">{m.drug_name} × {m.qty}</Badge>
                          ))}
                          {rx.medications?.length > 3 && <span className="text-xs text-slate-400">+{rx.medications.length - 3}</span>}
                        </div>
                      </td>
                      <td className="py-2 px-2"><Badge className={STATUS_BADGE[rx.status] || ''} variant="outline">{rx.status}</Badge></td>
                      <td className="py-2 px-2 text-xs text-slate-500">{rx.created_at ? format(new Date(rx.created_at), 'dd MMM yy HH:mm') : ''}</td>
                      <td className="py-2 px-2 text-right">
                        {rx.status === 'pending' && canDispense && (
                          <Button size="sm" variant="outline" onClick={() => handToPharmacy(rx)} data-testid={`rx-hand-${rx.id}`}>
                            <Send size={14} className="mr-1" /> Hand to Pharmacy
                          </Button>
                        )}
                        {rx.status === 'dispensed' && (
                          <span className="inline-flex items-center text-emerald-600 text-xs"><CheckCircle2 size={14} className="mr-1" /> Dispensed</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <CreateRxDialog open={createOpen} onOpenChange={setCreateOpen} onCreated={fetchRx} />
    </div>
  );
}

const CreateRxDialog = ({ open, onOpenChange, onCreated }) => {
  const [patients, setPatients] = useState([]);
  const [drugs, setDrugs] = useState([]);
  const [patientId, setPatientId] = useState('');
  const [diagnosis, setDiagnosis] = useState('');
  const [notes, setNotes] = useState('');
  const [meds, setMeds] = useState([{ drug_id: '', qty: 1, dosage: '', frequency: '', duration_days: 5, instructions: '' }]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const [p, d] = await Promise.all([
          patientsAPI.list({ limit: 500 }),
          pharmacyAPI.listDrugs(),
        ]);
        setPatients(p.data || []);
        setDrugs(d.data || []);
      } catch (err) {
        toast.error('Failed to load patient / drug lists');
      }
    })();
    // reset form
    setPatientId(''); setDiagnosis(''); setNotes('');
    setMeds([{ drug_id: '', qty: 1, dosage: '', frequency: '', duration_days: 5, instructions: '' }]);
  }, [open]);

  const addRow = () => setMeds([...meds, { drug_id: '', qty: 1, dosage: '', frequency: '', duration_days: 5, instructions: '' }]);
  const removeRow = (i) => setMeds(meds.filter((_, idx) => idx !== i));
  const updateRow = (i, patch) => setMeds(meds.map((m, idx) => idx === i ? { ...m, ...patch } : m));

  const handleSubmit = async () => {
    if (!patientId) { toast.error('Please select a patient'); return; }
    const valid = meds.filter(m => m.drug_id && m.qty > 0);
    if (valid.length === 0) { toast.error('Please add at least one medication'); return; }
    setSaving(true);
    try {
      await rxAPI.create({ patient_id: patientId, medications: valid, diagnosis, notes });
      toast.success('Prescription created');
      onCreated?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create prescription');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="new-rx-dialog">
        <DialogHeader><DialogTitle>New Prescription</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Patient *</Label>
            <Select value={patientId} onValueChange={setPatientId}>
              <SelectTrigger data-testid="rx-patient-select"><SelectValue placeholder="Select patient" /></SelectTrigger>
              <SelectContent>
                {patients.map(p => (
                  <SelectItem key={p.id} value={p.id}>{p.first_name} {p.last_name} {p.uhid ? `(${p.uhid})` : ''}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Diagnosis</Label>
            <Input value={diagnosis} onChange={(e) => setDiagnosis(e.target.value)} placeholder="e.g. Acute pharyngitis" data-testid="rx-diagnosis-input" />
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Medications *</Label>
              <Button type="button" size="sm" variant="outline" onClick={addRow} data-testid="rx-add-med-btn"><Plus size={14} className="mr-1" /> Add</Button>
            </div>
            {meds.map((m, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-end p-2 bg-slate-50 rounded-lg">
                <div className="col-span-12 md:col-span-4">
                  <Label className="text-xs">Drug</Label>
                  <Select value={m.drug_id} onValueChange={(v) => updateRow(i, { drug_id: v })}>
                    <SelectTrigger data-testid={`rx-med-drug-${i}`}><SelectValue placeholder="Drug" /></SelectTrigger>
                    <SelectContent>
                      {drugs.map(d => (
                        <SelectItem key={d.id} value={d.id}>{d.name} {d.strength ? `${d.strength}` : ''} {d.dosage_form ? `(${d.dosage_form})` : ''}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-3 md:col-span-1">
                  <Label className="text-xs">Qty</Label>
                  <Input type="number" min={1} value={m.qty} onChange={(e) => updateRow(i, { qty: parseInt(e.target.value || '0', 10) })} data-testid={`rx-med-qty-${i}`} />
                </div>
                <div className="col-span-4 md:col-span-2">
                  <Label className="text-xs">Dosage</Label>
                  <Input value={m.dosage} onChange={(e) => updateRow(i, { dosage: e.target.value })} placeholder="1 tab" />
                </div>
                <div className="col-span-5 md:col-span-2">
                  <Label className="text-xs">Frequency</Label>
                  <Input value={m.frequency} onChange={(e) => updateRow(i, { frequency: e.target.value })} placeholder="BID / 1-0-1" />
                </div>
                <div className="col-span-3 md:col-span-1">
                  <Label className="text-xs">Days</Label>
                  <Input type="number" min={0} value={m.duration_days || ''} onChange={(e) => updateRow(i, { duration_days: parseInt(e.target.value || '0', 10) })} />
                </div>
                <div className="col-span-12 md:col-span-1 flex justify-end">
                  {meds.length > 1 && <Button type="button" size="icon" variant="ghost" onClick={() => removeRow(i)} data-testid={`rx-remove-med-${i}`}><Trash2 size={14} className="text-rose-500" /></Button>}
                </div>
                <div className="col-span-12">
                  <Input value={m.instructions} onChange={(e) => updateRow(i, { instructions: e.target.value })} placeholder="Special instructions (after meals, with water…)" />
                </div>
              </div>
            ))}
          </div>
          <div>
            <Label>Notes</Label>
            <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Optional notes for the pharmacist or patient" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="rx-save-btn">{saving ? 'Saving…' : 'Save Prescription'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
